import { Component, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Logo } from '@/components/shared/Logo'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } })
  }

  render() {
    if (this.state.error) {
      return (
        <ErrorScreen
          error={this.state.error}
          onReset={() => this.setState({ error: null })}
        />
      )
    }
    return this.props.children
  }
}

export function ErrorScreen({ error, onReset }: { error?: Error; onReset?: () => void }) {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6">
      <div className="mb-8">
        <Logo size={44} theme="light" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 w-full max-w-md text-center">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-50 mx-auto mb-4">
          <AlertTriangle size={22} className="text-red-500" />
        </div>

        <h1 className="text-lg font-semibold text-slate-900 mb-2">Something went wrong</h1>
        <p className="text-sm text-slate-500 mb-6 leading-relaxed">
          An unexpected error occurred. Try refreshing the page — if the problem keeps happening, please get in touch.
        </p>

        <div className="flex gap-3 justify-center">
          <Button variant="outline" onClick={() => window.location.reload()}>
            Refresh page
          </Button>
          {onReset && (
            <Button onClick={() => { onReset(); window.location.href = '/' }}>
              Go to home
            </Button>
          )}
        </div>

        {error?.message && (
          <details className="mt-6 text-left">
            <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600 transition-colors">
              Error details
            </summary>
            <pre className="mt-2 text-xs text-slate-500 bg-slate-50 rounded-lg p-3 overflow-auto whitespace-pre-wrap break-all">
              {error.message}
            </pre>
          </details>
        )}
      </div>
    </div>
  )
}
